import type { CSSProperties } from 'react'
import { imageUrl } from './products'
import vectors from '../../public/shop/hand/vectors.json'
import wordTitles from '../../public/shop/hand/title-words.json'

export function HandwrittenTitle({ title, artwork }: { title: string; artwork: keyof typeof wordTitles | 'shirt' }) {
  const words = artwork === 'shirt' ? wordTitles.riley.slice(0, 4) : wordTitles[artwork]
  return (
    <p className="shop-cart-title">
      <span className="sr-only">{title}{artwork === 'shirt' ? '' : ' zine'}</span>
      <span aria-hidden="true">
        {words.map((word, index) => (
          <span key={word.file}>
            {index > 0 ? ' ' : null}
            <img className="shop-hand-word" src={imageUrl(`hand/${word.file}`)} alt="" draggable={false}
              width={word.width} height={word.height}
              style={{ width: `${word.width / 100}em` }} />
          </span>
        ))}
        {artwork === 'shirt' && <>
          {' '}<ShirtDescriptorWord word="embroidered" />
          {' '}<ShirtDescriptorWord word="tee" />
        </>}
      </span>
    </p>
  )
}

// Two windows onto the original photograph, leveled by the same 9° as the
// product caption. The padded ink bounds share a scale and baseline, so each
// word wraps independently without changing the lettering or clipping strokes.
const shirtDescriptorWords = {
  embroidered: { sourceX: 0, sourceWidth: 1220, left: -46, top: 179, width: 1186, height: 258 },
  tee: { sourceX: 1220, sourceWidth: 339, left: 37, top: 39, width: 273, height: 209 },
}

function ShirtDescriptorWord({ word }: { word: keyof typeof shirtDescriptorWords }) {
  const crop = shirtDescriptorWords[word]
  const scale = 75 / shirtDescriptorWords.embroidered.height
  const width = crop.width * scale
  return (
    <svg className="shop-hand-word" viewBox={`0 0 ${width} 140`}
      width={width} height={140} style={{ width: `${width / 100}em` }}
      aria-hidden="true" focusable="false">
      <g transform={`translate(0 ${100 - crop.height * scale}) scale(${scale}) translate(${-crop.left} ${-crop.top}) rotate(9)`}>
        <svg width={crop.sourceWidth} height={435}
          viewBox={`${crop.sourceX} 0 ${crop.sourceWidth} 435`} overflow="hidden">
          <image href={imageUrl('hand/shirt-hand-embroidered-tee.webp')} width={1559} height={435} />
        </svg>
      </g>
    </svg>
  )
}

/** Compose the owner's traced digits for any integer, including cart totals. */
export function HandwrittenNumber({ value }: { value: number }) {
  return <HandwrittenValue text={String(value)} />
}

export function HandwrittenMoney({ value, hideZeroCents = false }: { value: number; hideZeroCents?: boolean }) {
  const decimals = hideZeroCents && Number.isInteger(value) ? 0 : 2
  return <HandwrittenValue text={`$${value.toFixed(decimals)}`} money />
}

function HandwrittenValue({ text, money = false }: { text: string; money?: boolean }) {
  const glyphs = text.split('').map(character => {
    const name = character === '$' ? 'currency-dollar' : character === '.' ? 'decimal-point' : `digit-${character}`
    const size = vectors[name as keyof typeof vectors]
    const height = character === '.' ? 4 : 32
    return { name, height, y: character === '.' ? 27 : 0, width: size.width / size.height * height }
  })
  const width = glyphs.reduce((sum, glyph) => sum + glyph.width, 0) + (glyphs.length - 1) * 3
  const displayHeight = money ? 20 : 26
  let x = 0
  return (
    <svg className={money ? 'shop-hand-number shop-hand-money' : 'shop-hand-number'}
      viewBox={`0 0 ${width} 32`} width={width * displayHeight / 32} height={displayHeight}
      role="img" aria-label={text}>
      {glyphs.map((glyph, index) => {
        const left = x
        x += glyph.width + 3
        return <image key={index} href={imageUrl(`hand/${glyph.name}.svg`)}
          x={left} y={glyph.y} width={glyph.width} height={glyph.height} />
      })}
    </svg>
  )
}

export function handFrameStyle(file: string): CSSProperties {
  return { backgroundImage: `url("${imageUrl(`hand/${file}.svg`)}")` }
}
